import type { AppliedFilterItem } from '@fashionnovateam/nova-ui-toolkit';
import { CHECKBOX_FILTERS, CHECKBOX_PROPERTIES } from './filter-config';
import type { SampleRequestFilterValues, AdvancedFilterItem } from './types';

function toBackendValues(
  displayLabels: string[],
  map: Record<string, string[]>,
): string[] {
  return displayLabels.flatMap((label) => map[label] ?? []);
}

function toDisplayLabels(
  backendValues: string[],
  map: Record<string, string[]>,
): string[] {
  const backendSet = new Set(backendValues);
  return Object.entries(map)
    .filter(([, values]) => values.some((v) => backendSet.has(v)))
    .map(([label]) => label);
}

function toArray(value: AppliedFilterItem['value']): Array<string | number> {
  if (Array.isArray(value)) return value;
  if (value != null) return [value as string | number];
  return [];
}

export function buildAppliedFilters(
  value: SampleRequestFilterValues,
): AppliedFilterItem[] {
  const items: AppliedFilterItem[] = [];

  CHECKBOX_FILTERS.forEach(({ property, backendMap }) => {
    const raw = value[property];
    const arr = Array.isArray(raw) ? raw : [];
    if (arr.length === 0) return;

    const displayValues = backendMap
      ? toDisplayLabels(arr as string[], backendMap)
      : arr;

    items.push({
      id: `${property}-1`,
      property,
      rule: 'in',
      value: displayValues as AppliedFilterItem['value'],
    });
  });

  if (value.filters) {
    try {
      const parsed: AdvancedFilterItem[] = JSON.parse(value.filters);
      parsed.forEach((f, i) => {
        items.push({
          id: `${f.property}-${i}`,
          property: f.property,
          rule: f.rule,
          value: f.value as AppliedFilterItem['value'],
        });
      });
    } catch {
      console.log('Failed to parse filter');
    }
  }

  return items;
}

export function translateFiltersToValues(
  appliedFilters: AppliedFilterItem[],
): SampleRequestFilterValues {
  const result: SampleRequestFilterValues = {
    quoteStatus: [],
    prevSampleStatus: [],
    counterSampleStatus: [],
    priorities: [],
    vendorContacts: [],
    filters: '',
  };

  const advancedItems: AdvancedFilterItem[] = [];

  for (const filter of appliedFilters) {
    if (
      !CHECKBOX_PROPERTIES.has(
        filter.property as keyof SampleRequestFilterValues,
      )
    ) {
      advancedItems.push({
        property: filter.property,
        rule: filter.rule,
        value: filter.value as AdvancedFilterItem['value'],
      });
    } else if (filter.property === 'vendorContacts') {
      result.vendorContacts = toArray(filter.value).map(Number);
    } else {
      const values = toArray(filter.value);
      const config = CHECKBOX_FILTERS.find(
        (c) => c.property === filter.property,
      );
      const backendValues = config?.backendMap
        ? toBackendValues(values as string[], config.backendMap)
        : values;
      const key = filter.property as keyof Omit<
        SampleRequestFilterValues,
        'vendorContacts' | 'filters'
      >;
      result[key] = backendValues as string[];
    }
  }

  result.filters =
    advancedItems.length > 0 ? JSON.stringify(advancedItems) : '';
  return result;
}
